/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaAreaExecutionsWarm.ts
 * FILE_ID: JOBHUB-QA-AREA-EXECS-WARM-001
 * PURPOSE: Load every area of the open QA in one request and fill the per-area cache.
 * LANDMINE: ['qa-area-exec', id] stays the only key a screen reads. This hook
 *   only fills it. Without the bulk endpoint, or after it fails, each area is
 *   fetched on its own as before.
 */
import { useEffect } from 'react'
import { useQuery, type QueryClient } from '@tanstack/react-query'
import type { QaApi, QaAreaExecution } from '../../qa/qaApi'
import { currentWriteGeneration, keepCachedArea } from './qaExecutionCache'

export const QA_AREA_EXECS_KEY = 'qa-area-execs'

/** Long enough that a just-filled area is not fetched again by its own query. */
export const QA_AREA_EXEC_FRESH_MS = 60_000

type WarmApi = Pick<QaApi, 'getAreaExecution'> & Partial<Pick<QaApi, 'listAreaExecutions'>>

export function useQaAreaExecutionsWarm({
  api,
  inspectionId,
  areaIds,
  queryClient,
}: {
  api: WarmApi
  inspectionId: string | null
  areaIds: readonly string[]
  queryClient: QueryClient
}) {
  const hasBulk = typeof api.listAreaExecutions === 'function'
  const bulk = useQuery({
    queryKey: [QA_AREA_EXECS_KEY, inspectionId],
    queryFn: async () => {
      const rows = await (api.listAreaExecutions as NonNullable<WarmApi['listAreaExecutions']>)(
        inspectionId as string,
        { photos: false },
      )
      for (const row of rows) {
        const started = currentWriteGeneration(row.id)
        const cached = queryClient.getQueryData<QaAreaExecution>(['qa-area-exec', row.id])
        // Reused from: packages/job-hub/src/jobs/tabs/job-qa/qaExecutionCache.ts (keepCachedArea)
        const next = keepCachedArea(row.id, started, row, cached)
        if (next !== cached) queryClient.setQueryData(['qa-area-exec', row.id], next)
      }
      return rows.map((row) => row.id)
    },
    enabled: hasBulk && Boolean(inspectionId),
    retry: false,
  })
  const fallback = !hasBulk || bulk.isError
  // A joined key, not the array: the areas query hands back a new array with
  // the same ids on every render.
  const areaKey = areaIds.join('|')
  useEffect(() => {
    if (!fallback || !areaKey) return
    for (const id of areaKey.split('|')) {
      void queryClient.prefetchQuery({
        queryKey: ['qa-area-exec', id],
        queryFn: async () => {
          const started = currentWriteGeneration(id)
          // Reused from: packages/job-hub/src/jobs/tabs/job-qa/qaExecutionCache.ts (keepCachedArea)
          const fetched = await api.getAreaExecution(id, { photos: false })
          return keepCachedArea(id, started, fetched, queryClient.getQueryData(['qa-area-exec', id]))
        },
        staleTime: QA_AREA_EXEC_FRESH_MS,
      })
    }
  }, [fallback, areaKey, api, queryClient])
  return { ready: !inspectionId || fallback || bulk.isSuccess }
}

/** True when the open QA's areas are being kept fresh by one bulk request. */
export function qaBulkExecutionsLive(queryClient: QueryClient): boolean {
  if (typeof queryClient.getQueryCache !== 'function') return false
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: [QA_AREA_EXECS_KEY], type: 'active' })
    .some((query) => query.state.status === 'success')
}
